import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from "react";
import * as atlas from "azure-maps-control";
import "azure-maps-control/dist/atlas.min.css";
import {
  IconButton,
  Button,
  Dialog,
  DialogContent,
  Typography,
  CircularProgress
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BlockIcon from "@mui/icons-material/Block";
import SearchBox from "./SearchBox";
import "./AzureMapComponent.css";
import { polygon as turfPolygon } from "@turf/turf";

const mapSubscriptionKey = process.env.REACT_APP_AZURE_MAPS_KEY;

const AzureMap = forwardRef(({
  pins,
  statusFilters,
  handleStatusFilterChangeButton,
  salesList,
  selectedSalesperson,
  setSelectedSalesperson,
  fetchData,
  // Isochrone props from parent
  mergedIsochrones,
  updateIsochronePercentages,
  // Common utility functions from parent
  isTrue,
  generateImageId,
  getCRMLink,
  passesFilters,
  // New cluster config from parent
  clusterConfig,
  initialPosition,
  onPositionChange
}, ref) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const azureDataSourceRef = useRef(null);
  const azureSearchedDSRef = useRef(null);
  const [selectedPin, setSelectedPin] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [mapLoading, setMapLoading] = useState(true);
  const pinsLoadTimerRef = useRef(null);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    getCurrentBounds: () => {
      if (!mapRef.current) return null;
      return mapRef.current.getCamera().bounds;
    },
    getVisibleGreenPins: (bounds) => {
      if (!bounds || !azureDataSourceRef.current) return [];
      const [w, s, e, n] = bounds;
      
      const shapes = azureDataSourceRef.current.getShapes() || [];
      const visiblePins = shapes
        .filter((shape) => {
          if (shape.getProperties()?.cluster) return false;
          const [lon, lat] = shape.getCoordinates();
          return lon >= w && lon <= e && lat >= s && lat <= n;
        })
        .map((shape) => shape.getProperties());

      const greenWonPins = visiblePins.filter(
        (p) =>
          (p.StatusReason === "100000000" || p.StatusReason === "5") &&
          p.Status === "0"
      );
      
      return greenWonPins
        .map((p) => ({
          lat: parseFloat(p.Latitude),
          lng: parseFloat(p.Longitude)
        }))
        .filter(({ lat, lng }) => !isNaN(lat) && !isNaN(lng));
    }
  }));

  /* ------------- POPULATE AZURE PINS ------------- */
  const populatePinsAzure = (data, retryCount = 0) => {
    if (!azureDataSourceRef.current) {
      console.log("Data source not yet available");
      if (retryCount < 3) { // Try a few times before giving up
        setTimeout(() => populatePinsAzure(data, retryCount + 1), 500);
      }
      return;
    }
    
    if (!mapReady) {
      console.log("Map not fully ready yet");
      if (retryCount < 3) {
        setTimeout(() => populatePinsAzure(data, retryCount + 1), 500);
      }
      return;
    }
    
    console.log("Populating pins:", data.length);
    
    try {
      const features = data
        .filter(passesFilters)
        .map((pin) => {
          const lat = parseFloat(pin.Latitude);
          const lon = parseFloat(pin.Longitude);
          if (isNaN(lat) || isNaN(lon)) return null;
          return {
            type: "Feature",
            geometry: { type: "Point", coordinates: [lon, lat] },
            properties: { ...pin, image: generateImageId(pin) }
          };
        })
        .filter(Boolean);
      
      // Clear and add features in a single operation
      azureDataSourceRef.current.clear();
      if (features.length > 0) {
        azureDataSourceRef.current.add(features);
        console.log("Successfully added features:", features.length);
      } else {
        console.log("No features to add after filtering");
      }
    } catch (error) {
      console.error("Error populating pins:", error);
      if (retryCount < 3) {
        console.log("Retrying pin population...");
        setTimeout(() => populatePinsAzure(data, retryCount + 1), 500);
      }
    }
  };

  /* ------------- SEARCH LOCATION CENTER ---------- */
  const handleLocationSelect = (pos, prop = null) => {
    if (mapRef.current) {
      mapRef.current.setCamera({
        center: [pos.lon, pos.lat],
        zoom: prop?.type === "street" ? 16 : prop?.type === "city" ? 10 : 10
      });
      if (azureSearchedDSRef.current) {
        azureSearchedDSRef.current.clear();
        azureSearchedDSRef.current.add({
          type: "Feature",
          geometry: { type: "Point", coordinates: [pos.lon, pos.lat] },
          properties: { ...(prop || {}), isSearched: true }
        });
      }
    }
  };

  /* ------------- CLEAR ISOCHRONE LAYERS ------------- */
  const clearIsochroneLayersAzure = () => {
    ["mergedIsochroneLayer20", "mergedIsochroneLayer15", "mergedIsochroneLayer10", "mergedIsochroneLayer5"].forEach((id) => {
      const l = mapRef.current?.layers.getLayerById(id);
      if (l) mapRef.current.layers.remove(l);
    });
    ["mergedIsochroneSource20", "mergedIsochroneSource15", "mergedIsochroneSource10", "mergedIsochroneSource5"].forEach((id) => {
      const s = mapRef.current?.sources.getById(id);
      if (s) mapRef.current.sources.remove(s);
    });
  };

  /* ------------- ISO RENDER – AZURE ------------- */
  const renderIsochrones = () => {
    if (!mapRef.current || !mergedIsochrones) return;
    
    clearIsochroneLayersAzure();

    const addLayer = (dur, color, srcId, layerId) => {
      const arr = mergedIsochrones[dur] || mergedIsochrones[Number(dur)];
      if (!arr?.length) return;
      const feats = [];
      arr.forEach((poly) => {
        const coords = poly.map((pt) => [pt.Lng || pt.lng, pt.Lat || pt.lat]);
        if (
          coords[0][0] !== coords[coords.length - 1][0] ||
          coords[0][1] !== coords[coords.length - 1][1]
        )
          coords.push(coords[0]);
        feats.push(turfPolygon([coords]));
      });
      if (!feats.length) return;
      const src = new atlas.source.DataSource(srcId);
      feats.forEach((f) => src.add(new atlas.data.Feature(f.geometry)));
      mapRef.current.sources.add(src);
      mapRef.current.layers.add(
        new atlas.layer.PolygonLayer(src, layerId, {
          fillColor: color,
          fillOpacity: 0.5,
          strokeColor: color,
          strokeWidth: 2
        })
      );
    };

    addLayer("20", "rgba(219,23,19,0.5)", "mergedIsochroneSource20", "mergedIsochroneLayer20");
    addLayer("15", "rgba(247,139,7,0.8)", "mergedIsochroneSource15", "mergedIsochroneLayer15");
    addLayer("10", "rgba(251,198,47,0.8)", "mergedIsochroneSource10", "mergedIsochroneLayer10");
    addLayer("5", "rgba(76,175,80,0.9)", "mergedIsochroneSource5", "mergedIsochroneLayer5");

    // Ensure pins appear on top of isochrones
    ["pin-symbol-layer", "searched-symbol-layer", "cluster-bubble-layer", "cluster-text-layer"].forEach((id) => {
      const l = mapRef.current.layers.getLayerById(id);
      if (l) mapRef.current.layers.move(l.id);
    });
    
    // Calculate initial percentages
    const bounds = ref.current.getCurrentBounds();
    if (bounds) updateIsochronePercentages(bounds);
  };

  useEffect(() => {
    if (mergedIsochrones) {
      renderIsochrones();
    } else {
      clearIsochroneLayersAzure();
    }
  }, [mergedIsochrones]);

  useEffect(() => {
    if (!mapRef.current || !mergedIsochrones) return;
    
    const updatePercentagesOnMapChange = () => {
      if (!mapRef.current || !mergedIsochrones) return;
      const bounds = ref.current.getCurrentBounds();
      if (bounds) updateIsochronePercentages(bounds);
    };

    mapRef.current.events.add('zoomend', updatePercentagesOnMapChange);
    mapRef.current.events.add('moveend', updatePercentagesOnMapChange);

    // Initial calculation
    updatePercentagesOnMapChange();

    return () => {
      if (mapRef.current) {
        mapRef.current.events.remove('zoomend', updatePercentagesOnMapChange);
        mapRef.current.events.remove('moveend', updatePercentagesOnMapChange);
      }
    };
  }, [mergedIsochrones]);

  // Clean up any pending timers when component unmounts
  useEffect(() => {
    return () => {
      if (pinsLoadTimerRef.current) {
        clearTimeout(pinsLoadTimerRef.current);
      }
    };
  }, []);

  /* ------------------ AZURE MAP INIT ------------------- */
  useEffect(() => {
    console.log("Initializing Azure map");
    setMapReady(false);
    setMapLoading(true);
    
    // Clear any pending timers when re-initializing the map
    if (pinsLoadTimerRef.current) {
      clearTimeout(pinsLoadTimerRef.current);
    }
    
    const azureMap = new atlas.Map("azureMapContainer", {
      center: initialPosition.center,
      zoom: initialPosition.zoom,
      style: "satellite",
      authOptions: { authType: "subscriptionKey", subscriptionKey: mapSubscriptionKey },
      showLogo: false,
      language: "en-US"
    });

    azureMap.events.add('move', () => {
      const camera = azureMap.getCamera();
      onPositionChange({
        center: [camera.center[0], camera.center[1]],
        zoom: camera.zoom
      });
    });

    azureMap.events.add("ready", () => {
      console.log("Azure map ready event fired");
      
      const dataSource = new atlas.source.DataSource(null, {
        cluster: clusterConfig.enabled,
        clusterRadius: clusterConfig.radius,
        clusterMinPoints: clusterConfig.minPoints || 2
      });
      azureMap.sources.add(dataSource);
      azureDataSourceRef.current = dataSource;

      const searchedDS = new atlas.source.DataSource();
      azureMap.sources.add(searchedDS);
      azureSearchedDSRef.current = searchedDS;

      /* load pin icons */
      const loadImage = (map, id, url) =>
        new Promise((res, rej) => {
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.onload = () => {
            map.imageSprite.add(id, img)
              .then(res)
              .catch(err => {
                console.error(`Error adding sprite ${id}:`, err);
                res(); // Resolve anyway to continue the process
              });
          };
          img.onerror = (err) => {
            console.error(`Error loading image ${id}:`, err);
            res(); // Resolve anyway to continue the process
          };
          img.src = url;
        });

      const prefixes = ["green-pin", "yellow-pin", "red-pin", "blue-pin"];
      const suffixes = ["garage", "fence", "fee", "REIT"];
      const combos = new Set();
      
      // Add basic pin types
      prefixes.forEach(p => combos.add(p));
      
      // Add all combinations
      const total = 1 << suffixes.length;
      for (let i = 0; i < total; i++) {
        const suf = [];
        for (let j = 0; j < suffixes.length; j++)
          if (i & (1 << j)) suf.push(suffixes[j]);
        
        // Add combinations for all prefixes
        ["green-pin", "yellow-pin", "red-pin"].forEach((p) => {
          if (suf.length > 0) {
            combos.add(`${p}-${suf.join("-")}`);
          }
          
          // Add inactive variants for green pins
          if (p === "green-pin") {
            combos.add("green-pin-inactive");
            if (suf.length > 0) {
              combos.add(`green-pin-inactive-${suf.join("-")}`);
            }
          }
        });
      }

      console.log("Loading map icons:", Array.from(combos).join(", "));

      Promise.all(
        Array.from(combos).map((id) => loadImage(azureMap, id, `/icons/${id}.png`))
      )
        .then(() => {
          console.log("All icons loaded, adding map layers");
          
          try {
            azureMap.layers.add(
              new atlas.layer.BubbleLayer(dataSource, "cluster-bubble-layer", {
                filter: ["has", "point_count"],
                radius: [
                  "step", ["get", "point_count"], 
                  clusterConfig.sizes.small, 
                  clusterConfig.sizeRanges.small, clusterConfig.sizes.medium, 
                  clusterConfig.sizeRanges.medium, clusterConfig.sizes.large
                ],
                color: [
                  "step", ["get", "point_count"], 
                  clusterConfig.colors.small, 
                  clusterConfig.sizeRanges.small, clusterConfig.colors.medium, 
                  clusterConfig.sizeRanges.medium, clusterConfig.colors.large
                ],
                strokeWidth: 2,
                strokeColor: "white"
              })
            );
            
            azureMap.layers.add(
              new atlas.layer.SymbolLayer(dataSource, "cluster-text-layer", {
                filter: ["has", "point_count"],
                iconOptions: { image: "none" },
                textOptions: {
                  textField: "{point_count_abbreviated}",
                  offset: [0, 0.4],
                  color: "black",
                  font: ["StandardFont-Bold"],
                  size: 12
                }
              })
            );
            
            const pinLayer = new atlas.layer.SymbolLayer(
              dataSource,
              "pin-symbol-layer",
              {
                filter: ["!", ["has", "point_count"]],
                iconOptions: { image: ["get", "image"], allowOverlap: true, size: 0.7 }
              }
            );
            azureMap.layers.add(pinLayer);
            
            const searchedLayer = new atlas.layer.SymbolLayer(
              searchedDS,
              "searched-symbol-layer",
              {
                filter: ["has", "isSearched"],
                iconOptions: { image: "blue-pin", allowOverlap: true, size: 1.0 }
              }
            );
            azureMap.layers.add(searchedLayer);

            azureMap.events.add("click", pinLayer, (e) => {
              if (!e.shapes?.length) return;
                const props = e.shapes[0].getProperties();
                setSelectedPin(props);
                setDialogOpen(true);
              
            });
            
            // azureMap.events.add("click", searchedLayer, (e) => {
            //   if (e.shapes?.length) {
            //     const props = e.shapes[0].getProperties();
            //     setSelectedPin(props);
            //     setDialogOpen(true);
            //   }
            // });

            azureMap.controls.add(
              [
                new atlas.control.ZoomControl(),
                new atlas.control.CompassControl(),
                new atlas.control.StyleControl({
                  mapStyles: [
                    'road',
                    'grayscale_dark',
                    'grayscale_light',
                    'night',
                    'satellite',
                    'satellite_road',
                    'road_shaded_relief',
                  ],
                }),
              ],
              {
                position: 'bottom-right'
              }
            );

            mapRef.current = azureMap;
            setMapReady(true);
            setMapLoading(false);
            
            console.log("Map initialization complete, ready to add pins");
            
            // Now that the map is fully initialized, populate pins
            if (pins.length > 0) {
              // Use a delay to ensure everything is fully rendered
              pinsLoadTimerRef.current = setTimeout(() => {
                populatePinsAzure(pins);
              }, 500);
            }
          } catch (err) {
            console.error("Error setting up map layers:", err);
            setMapLoading(false);
          }
        })
        .catch((e) => {
          console.error("Image load error:", e);
          setMapLoading(false);
        });
    });

    return () => {
      if (mapRef.current) {
        console.log("Disposing Azure map");
        mapRef.current.dispose();
        mapRef.current = null;
      }
      setMapReady(false);
    };
  }, [clusterConfig]); // Added clusterConfig as a dependency

  /* --------- UPDATE PINS ON CHANGE ---------- */
  useEffect(() => {
    console.log("Pins changed or filters changed, mapReady:", mapReady);
    if (mapReady && azureDataSourceRef.current) {
      // Clear any existing timer
      if (pinsLoadTimerRef.current) {
        clearTimeout(pinsLoadTimerRef.current);
      }
      
      // Add a small delay before populating pins to ensure all Azure Map components are ready
      pinsLoadTimerRef.current = setTimeout(() => {
        populatePinsAzure(pins);
      }, 300);
    }
  }, [pins, statusFilters, selectedSalesperson, mapReady]);

  return (
    <div className="map-fullscreen-container" ref={containerRef}>
      {/* Show loading indicator while map is initializing */}
      {mapLoading && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 2000,
          backgroundColor: 'rgba(255,255,255,0.7)',
          padding: '20px',
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '10px'
        }}>
          <CircularProgress size={40} />
          <div>Loading map...</div>
        </div>
      )}

      {/* --- FILTERS ROW --- */}
      <div className="filters-overlay">
        <div className="filters-row">
          <div className="filters">
            <Button
              className={`filter-button green-filter ${
                statusFilters.Green ? "active" : ""
              }`}
              onClick={() => handleStatusFilterChangeButton("Green")}
              startIcon={
                <img
                  src="/icons/won-icon.png"
                  alt="Won"
                  className="icon"
                />
              }
            >
              Won
            </Button>
            <Button
              className={`filter-button yellow-filter ${
                statusFilters.Yellow ? "active" : ""
              }`}
              onClick={() => handleStatusFilterChangeButton("Yellow")}
              startIcon={
                <img
                  src="/icons/in-progress-icon.png"
                  alt="In Progress"
                  className="icon"
                />
              }
            >
              In Progress
            </Button>
            <Button
              className={`filter-button red-filter ${
                statusFilters.Red ? "active" : ""
              }`}
              onClick={() => handleStatusFilterChangeButton("Red")}
              startIcon={
                <img src="/icons/lost-icon.png" alt="Lost" className="icon" />
              }
            >
              Lost
            </Button>
            <Button
              className={`filter-button reit-filter ${
                statusFilters.REIT ? "active" : ""
              }`}
              onClick={() => handleStatusFilterChangeButton("REIT")}
              startIcon={
                <img
                  src="/icons/reit-icon.png"
                  alt="REIT"
                  className="icon"
                  style={{ width: 16, height: 16 }}
                />
              }
            >
              REIT
            </Button>
          </div>
          <div className="filters">
            <Button
              className={`filter-button garage-filter ${
                statusFilters.Garage ? "active" : ""
              }`}
              onClick={() => handleStatusFilterChangeButton("Garage")}
              startIcon={
                <img
                  src="/icons/garage-icon.png"
                  alt="Garage"
                  style={{ width: 18, height: 18 }}
                />
              }
            >
              Garage
            </Button>
            <Button
              className={`filter-button gated-filter ${
                statusFilters.GatedFenced ? "active" : ""
              }`}
              onClick={() => handleStatusFilterChangeButton("GatedFenced")}
              startIcon={
                <img
                  src="/icons/fence-icon.png"
                  alt="Gated/Fenced"
                  style={{ width: 16, height: 14 }}
                />
              }
            >
              Gated/Fenced
            </Button>
            <Button
              className={`filter-button fee-filter ${
                statusFilters.Fee ? "active" : ""
              }`}
              onClick={() => handleStatusFilterChangeButton("Fee")}
              startIcon={<AttachMoneyIcon className="icon" />}
            >
              Fee
            </Button>
            {statusFilters.Green && (
              <Button
                className={`filter-button inactive-filter ${
                  statusFilters.NotRegistered ? "active" : ""
                }`}
                onClick={() =>
                  handleStatusFilterChangeButton("NotRegistered")
                }
                startIcon={<BlockIcon className="icon" />}
              >
                Not Registered
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* --- SEARCH BOX --- */}
      <div className="search-overlay">
        <SearchBox onLocationSelect={handleLocationSelect} />
      </div>

      {/* --- MAP CANVAS --- */}
      <div id="azureMapContainer" className="map-container" />

      {/* --- CLUSTER INFO TOOLTIP --- */}
      <div className="cluster-info" style={{ 
        position: 'absolute', 
        bottom: '40px', 
        right: '10px', 
        backgroundColor: 'rgba(255,255,255,0.8)',
        padding: '5px 10px',
        borderRadius: '4px',
        fontSize: '12px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        zIndex: 1000
      }}>
        <div>Click on clusters to zoom in and view individual properties</div>
      </div>

      {/* -------- PROPERTY DIALOG -------- */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 16,
            overflow: "hidden",
            position: "fixed",
            bottom: 16,
            left: "50%",
            transform: "translateX(-50%)",
            margin: 0,
            padding: "8px 16px",
            boxShadow: "0px 4px 12px rgba(0,0,0,0.1)"
          }
        }}
        BackdropProps={{ style: { backgroundColor: "transparent" } }}
      >
        <DialogContent style={{ display: "flex", padding: 0 }}>
          {selectedPin && (
            <>
              <div style={{ width: "40%", position: "relative", height: "80%" }}>
                <img
                  src="/icons/robo-icon.png"
                  alt="Robotaxi"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              </div>
              <div
                style={{
                  width: "60%",
                  padding: 16,
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center"
                }}
              >
                <IconButton
                  onClick={() => setDialogOpen(false)}
                  style={{ position: "absolute", top: -5, right: -5 }}
                >
                  <CloseIcon />
                </IconButton>
                <Typography
                  variant="h6"
                  style={{ fontWeight: "bold", fontSize: "1.2rem", lineHeight: 1.6 }}
                >
                  {`${selectedPin.PropertyAddress},${selectedPin.City}, ${selectedPin.State} ${selectedPin.Zip}`}
                </Typography>
                {/* <Typography
                variant="body2"
                style={{ fontSize: "1rem", color: "#555", lineHeight: 1.6 }}
                gutterBottom
                >
                Number of Parking Spaces:{" "}
                {Array.isArray(selectedPin?.coordinateDetails)
                ? selectedPin.coordinateDetails.filter(
                (coord) => coord.title === "PSL - Parking Space Location"
                ).length
                : "N/A"}
                </Typography> */}
                <Typography 
                 variant="body2"
                 style={{ fontSize: "1rem", color: "#555", lineHeight: 1.6 }}
                 gutterBottom
                 >
                 Number of Parking Spaces:{" "}
                 {selectedPin?.NumberOfParkingSpaces?.toString().trim() || "N/A"}
                </Typography>

                <Typography
                  variant="body2"
                  style={{ fontSize: "1rem", color: "#555", lineHeight: 1.6 }}
                  gutterBottom
                >
                {`Status: ${({ "100000000": "Won", "1": "In Progress", "2": "Lost" })[selectedPin?.StatusReason]}`}
                </Typography>

                <Typography variant="body2" style={{ fontSize: "1rem", color: "#555", lineHeight: 1.6, display: "flex", alignItems: "center", gap: "0.5rem" }}>
                {[
                selectedPin?.REIT === "True" ? <img src="/icons/reit-icon.png" alt="REIT" style={{ width: 16, height: 16 }} /> : null,
                selectedPin?.garage === "True" ? <img src="/icons/garage-icon.png" alt="REIT" style={{ width: 16, height: 16 }} /> : null,
                selectedPin?.GatedOrFenced === "True" ? <img src="/icons/fence-icon.png" alt="REIT" style={{ width: 16, height: 16 }} /> : null,
                selectedPin?.Fee === "True" ? (<AttachMoneyIcon style={{ fontSize: 16 }} />) : null,].filter(Boolean).map((item, index) => (
                <span key={index}>{item}</span>
                )) || "N/A"}
                </Typography>

 
                {selectedPin.Id && (
                  <Typography
                    variant="body2"
                    style={{
                      marginTop: 16,
                      color: "#0078D4",
                      cursor: "pointer",
                      fontSize: "1rem"
                    }}
                  >
                    <a
                      href={getCRMLink(selectedPin.Id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: "none",
                        color: "#0078D4",
                        fontWeight: "bolder",
                        fontSize: 15
                      }}
                    >
                      Click here to launch the CRM opportunity: <u>Link</u>
                    </a>
                  </Typography>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default AzureMap;
